import { useId, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  ScrollArea,
  Select,
  Text,
  TextArea,
  TextField
} from '@radix-ui/themes'
import { FileTextIcon } from '@radix-ui/react-icons'
import { useDropzone } from 'react-dropzone'
import { TRPCClientError } from '@trpc/client'
import cn from 'classnames'
import { Ops } from '@willie/schemas'
import { useAuth } from '~/auth/AuthProvider'
import { FieldShell } from '~/components/FieldShell'
import { MessageCallout } from '~/components/MessageCallout'
import { ContactPicker } from '~/features/contacts/components/ContactPicker'
import { useTRPC, useTRPCClient } from '~/utils/trpc'
import { LABEL_MAX_BYTES, removeReturnLabel, uploadReturnLabel } from '../service'
import { useInvalidateMaterialReturns } from '../useInvalidateMaterialReturns'

const trackingNumberSchema = Ops.MaterialReturns.createMaterialReturnInputSchema.shape.trackingNumber
const ccEmailSchema = Ops.MaterialReturns.createMaterialReturnInputSchema.shape.ccEmail

const PROVIDER_LABEL: Record<Ops.MaterialReturns.MaterialReturnProvider, string> = {
  shayp: 'Shayp (DHL)'
}

interface CreateMaterialReturnFormProps {
  orgId: string
  installationId: number
  defaultContactId: string | null
  onSent: () => void
}

interface CreateMaterialReturnDialogProps extends Omit<CreateMaterialReturnFormProps, 'onSent'> {
  trigger: ReactNode
}

// Rendered inside Dialog.Content, which unmounts on close, so every field starts fresh on reopen.
const CreateMaterialReturnForm = ({
  orgId,
  installationId,
  defaultContactId,
  onSent
}: CreateMaterialReturnFormProps) => {
  const trpc = useTRPC()
  const trpcClient = useTRPCClient()
  const { supabaseClient } = useAuth()
  const invalidateMaterialReturns = useInvalidateMaterialReturns()

  const [provider, setProvider] = useState<Ops.MaterialReturns.MaterialReturnProvider>('shayp')
  const [installationIds, setInstallationIds] = useState<number[]>([installationId])
  const [contactId, setContactId] = useState<string | null>(defaultContactId)
  const [ccEmailInput, setCcEmailInput] = useState('')
  const ccEmailDatalistId = useId()
  const [materialDescription, setMaterialDescription] = useState('')
  const [trackingNumberInput, setTrackingNumberInput] = useState('')
  const trackingNumber = trackingNumberSchema.safeParse(trackingNumberInput)

  const installationsQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions({ orgId, includeRetired: true })
  )
  const orgInstallations = installationsQuery.data ?? []
  const contactsQuery = useQuery(trpc.ops.contacts.list.queryOptions({ orgId }))
  const contactEmails = (contactsQuery.data ?? []).flatMap((contact) => (contact.email ? [contact.email] : []))
  const recipientEmail = contactsQuery.data?.find((contact) => contact.id === contactId)?.email ?? null

  const ccEmail = ccEmailInput.trim() || null
  const ccEmailError =
    ccEmail === null
      ? null
      : !ccEmailSchema.safeParse(ccEmail).success
        ? 'Enter a valid email address'
        : ccEmail.toLowerCase() === recipientEmail?.toLowerCase()
          ? 'The CC must differ from the recipient'
          : null

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!label || !contactId || !trackingNumber.success) {
        throw new Error('A label, a recipient and a waybill number are required')
      }
      const labelStoragePath = await uploadReturnLabel(supabaseClient, label)
      return trpcClient.ops.materialReturns.create
        .mutate({
          orgId,
          provider,
          installationIds,
          contactId,
          ccEmail,
          materialDescription,
          labelStoragePath,
          trackingNumber: trackingNumber.data
        })
        .catch(async (error: unknown) => {
          // Only a server-side rejection proves no return points at the label; a dropped response may follow a success.
          if (error instanceof TRPCClientError && error.data)
            await removeReturnLabel(supabaseClient, labelStoragePath)
          throw error
        })
    },
    onSuccess: () => {
      invalidateMaterialReturns(installationIds)
      onSent()
    }
  })
  const pending = createMutation.isPending

  const { getRootProps, getInputProps, isDragActive, acceptedFiles, fileRejections } = useDropzone({
    multiple: false,
    accept: { 'application/pdf': ['.pdf'] },
    maxSize: LABEL_MAX_BYTES,
    disabled: pending
  })
  const label = acceptedFiles[0] ?? null
  const fileError = fileRejections[0]?.errors[0]?.message ?? null

  const toggleInstallation = (id: number, checked: boolean) =>
    setInstallationIds((prev) => (checked ? [...prev, id] : prev.filter((existing) => existing !== id)))

  const canSubmit =
    installationIds.length > 0 &&
    contactId !== null &&
    ccEmailError === null &&
    materialDescription.trim().length > 0 &&
    label !== null &&
    trackingNumber.success

  return (
    <>
      <Flex direction="column" gap="4">
        <FieldShell label="Provider">
          <Select.Root
            value={provider}
            disabled={pending}
            onValueChange={(value) => setProvider(Ops.MaterialReturns.materialReturnProviderSchema.parse(value))}
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              {Ops.MaterialReturns.materialReturnProviderSchema.options.map((option) => (
                <Select.Item key={option} value={option}>
                  {PROVIDER_LABEL[option]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </FieldShell>

        <FieldShell label={`Installations (${installationIds.length} selected)`}>
          <ScrollArea
            type="auto"
            scrollbars="vertical"
            className="max-h-48 rounded-[var(--radius-3)] border border-[color:var(--gray-6)]"
          >
            <Flex direction="column" gap="2" p="2">
              {installationsQuery.isLoading ? (
                <Text size="2" color="gray">
                  Loading…
                </Text>
              ) : (
                orgInstallations.map((installation) => (
                  <Text as="label" size="2" key={installation.installation_id}>
                    <Flex gap="2" align="center">
                      <Checkbox
                        checked={installationIds.includes(installation.installation_id)}
                        disabled={pending}
                        onCheckedChange={(checked) =>
                          toggleInstallation(installation.installation_id, checked === true)
                        }
                      />
                      #{installation.installation_id} · {installation.site?.name ?? 'No site'} ·{' '}
                      {installation.name ?? 'Unnamed'}
                      {installation.retired_at ? (
                        <Badge color="gray" size="1">
                          Retired
                        </Badge>
                      ) : null}
                    </Flex>
                  </Text>
                ))
              )}
            </Flex>
          </ScrollArea>
        </FieldShell>

        <ContactPicker
          orgId={orgId}
          label="Recipient"
          value={contactId}
          onChange={setContactId}
          disabled={pending}
        />
        <FieldShell
          label="CC (optional)"
          hint={
            ccEmailError ? (
              <Text size="1" color="red">
                {ccEmailError}
              </Text>
            ) : null
          }
        >
          <TextField.Root
            type="email"
            placeholder="colleague@meetwillie.com"
            list={ccEmailDatalistId}
            value={ccEmailInput}
            disabled={pending}
            onChange={(event) => setCcEmailInput(event.target.value)}
          />
          <datalist id={ccEmailDatalistId}>
            {contactEmails.map((email) => (
              <option key={email} value={email} />
            ))}
          </datalist>
        </FieldShell>

        <FieldShell label="Material to return">
          <TextArea
            placeholder="e.g. 2 Shayp loggers and their cables"
            value={materialDescription}
            maxLength={Ops.MaterialReturns.MATERIAL_DESCRIPTION_MAX_LENGTH}
            disabled={pending}
            onChange={(event) => setMaterialDescription(event.target.value)}
          />
        </FieldShell>

        <FieldShell label="Return label (PDF)">
          <Box
            {...getRootProps()}
            p="4"
            className={cn(
              'cursor-pointer rounded-[var(--radius-3)] border border-dashed border-[color:var(--gray-7)] text-center',
              isDragActive && 'bg-[var(--accent-a3)]'
            )}
          >
            <input {...getInputProps()} />
            {label ? (
              <Flex gap="2" align="center" justify="center">
                <FileTextIcon />
                <Text size="2">{label.name}</Text>
              </Flex>
            ) : (
              <Text size="2" color="gray">
                Drop the label PDF here, or click to choose it
              </Text>
            )}
          </Box>
        </FieldShell>
        {fileError ? <MessageCallout color="red" message={fileError} /> : null}

        <FieldShell
          label="DHL waybill number"
          hint={
            trackingNumberInput.trim() !== '' && !trackingNumber.success ? (
              <Text size="1" color="red">
                {trackingNumber.error.issues[0]?.message}
              </Text>
            ) : null
          }
        >
          <TextField.Root
            placeholder="67 8293 1632"
            value={trackingNumberInput}
            disabled={pending}
            onChange={(event) => setTrackingNumberInput(event.target.value)}
          />
        </FieldShell>

        {createMutation.error ? <MessageCallout color="red" message={createMutation.error.message} /> : null}
      </Flex>

      <Flex justify="end" gap="2" mt="4">
        <Dialog.Close>
          <Button variant="soft" color="gray" disabled={pending}>
            Cancel
          </Button>
        </Dialog.Close>
        <Button loading={pending} disabled={!canSubmit} onClick={() => createMutation.mutate()}>
          Send return label
        </Button>
      </Flex>
    </>
  )
}

export const CreateMaterialReturnDialog = ({ trigger, ...formProps }: CreateMaterialReturnDialogProps) => {
  const [open, setOpen] = useState(false)

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content maxWidth="640px">
        <Dialog.Title>Request material return</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Emails the return label and the pickup link to the recipient, and adds the return to the timeline of
          every selected installation.
        </Dialog.Description>
        <CreateMaterialReturnForm {...formProps} onSent={() => setOpen(false)} />
      </Dialog.Content>
    </Dialog.Root>
  )
}
